import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthSession } from "@/lib/auth-client";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { Logo } from "@/components/Logo";
import { Typography } from "@/components/ui/Typography";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useUiStore } from "@/stores/ui.store";
import { formatNextDueDate } from "@/lib/dateUtils";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const PAYMENT_MODES = ["CASH", "UPI", "BANK_TRANSFER", "CHEQUE", "OTHER"] as const;

type TenantDashboard = {
  unit: {
    id: string;
    alias: string;
    unitNumber: string;
    propertyName: string;
    propertyId: string;
    rentAmount: number;
    dueDate: number;
  };
  rentDue: Array<{
    id: string;
    month: number;
    year: number;
    amount: number;
    status: string;
    paidDate: string | null;
    paymentMode: string | null;
    notes: string | null;
    dueDate: number;
  }>;
  expensesToPay: Array<{
    id: string;
    category: string;
    amount: number;
    date: string;
    notes: string | null;
  }>;
};

const CATEGORY_LABELS: Record<string, string> = {
  MAINTENANCE: "Maintenance",
  TAX: "Tax",
  INSURANCE: "Insurance",
  UTILITY: "Utility",
  WATER_BILL: "Water bill",
  OTHER: "Other",
};

export function TenantDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);
  const { session, isPending } = useAuthSession();
  const role = (session?.user as { role?: string } | undefined)?.role;

  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useQuery({
    queryKey: queryKeys.tenantDashboard(),
    queryFn: () => api.get<TenantDashboard>("/tenant/dashboard"),
    enabled: !!session?.user && role === "TENANT",
  });

  const [markPaidModal, setMarkPaidModal] = useState<{
    rentId: string;
    month: number;
    year: number;
  } | null>(null);
  const [paymentMode, setPaymentMode] = useState<typeof PAYMENT_MODES[number]>("UPI");
  const [notes, setNotes] = useState("");

  const markPaidMutation = useMutation({
    mutationFn: ({ rentId, paymentMode, notes }: { rentId: string; paymentMode: string; notes?: string }) =>
      api.patch(`/rent/${rentId}/mark-paid`, { paymentMode, notes }),
    onSuccess: () => {
      addToast({ type: "success", message: "Rent marked as paid" });
      setMarkPaidModal(null);
      setNotes("");
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantDashboard() });
    },
    onError: (err: Error) => {
      addToast({ type: "error", message: err instanceof ApiError ? err.message : "Failed to update" });
    },
  });

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      navigate("/login", { replace: true });
      return;
    }
    if (role === "OWNER") {
      navigate("/dashboard", { replace: true });
    }
  }, [isPending, session?.user, role, navigate]);

  function handleMarkPaid() {
    if (!markPaidModal) return;
    markPaidMutation.mutate({
      rentId: markPaidModal.rentId,
      paymentMode,
      notes: notes.trim() || undefined,
    });
  }

  if (isPending || !session?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  const name = (session.user as { name?: string | null }).name ?? (session.user as { email?: string }).email?.split("@")[0] ?? "Tenant";

  if (role !== "TENANT") {
    return null;
  }

  if (dashboardLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Link to="/tenant"><Logo size="md" /></Link>
            <span className="text-sm text-slate-500">Sign out</span>
          </div>
        </header>
        <main className="flex-1 p-6 max-w-4xl mx-auto w-full">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
          <div className="mt-6 h-48 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
          <div className="mt-6 h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
        </main>
      </div>
    );
  }

  if (dashboardError || !dashboard) {
    const message = dashboardError instanceof ApiError ? dashboardError.message : "Failed to load your dashboard.";
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Link to="/tenant"><Logo size="md" /></Link>
            <Link
              to="/login"
              onClick={async (e) => {
                e.preventDefault();
                const { authClient } = await import("@/lib/auth-client");
                await authClient.signOut();
                window.location.href = "/login";
              }}
              className="text-sm font-medium text-slate-600 dark:text-slate-300"
            >
              Sign out
            </Link>
          </div>
        </header>
        <main className="flex-1 p-6 max-w-4xl mx-auto w-full">
          <Typography variant="h1">Welcome, {name}</Typography>
          <div className="mt-6 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6">
            <p className="text-amber-800 dark:text-amber-200 font-medium">{message}</p>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              You may not be linked to a unit yet. Ask your landlord to add you as a tenant and link your account.
            </p>
          </div>
        </main>
      </div>
    );
  }

  const { unit, rentDue, expensesToPay } = dashboard;
  const pendingRent = rentDue.filter((r) => r.status === "PENDING");
  const paidRent = rentDue.filter((r) => r.status === "PAID");

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <Link to="/tenant" className="inline-block">
            <Logo size="md" />
          </Link>
          <Link
            to="/login"
            onClick={async (e) => {
              e.preventDefault();
              const { authClient } = await import("@/lib/auth-client");
              await authClient.signOut();
              window.location.href = "/login";
            }}
            className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
          >
            Sign out
          </Link>
        </div>
      </header>

      <main className="flex-1 p-6 max-w-4xl mx-auto w-full space-y-6">
        <div>
          <Typography variant="h1">Welcome, {name}</Typography>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Your unit and payment overview.
          </p>
        </div>

        {/* Unit & rent summary */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6">
          <Typography variant="h2" className="mb-1">{unit.propertyName}</Typography>
          <p className="text-sm text-slate-500 dark:text-slate-400">{unit.alias} (Unit {unit.unitNumber})</p>
          <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Rent: ₹{unit.rentAmount.toLocaleString("en-IN")}/month
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Due by the {unit.dueDate} of each month</p>
          <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">
            Next rent due: {formatNextDueDate(unit.dueDate)}
          </p>
        </div>

        {/* Rent due / Mark as paid */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6">
          <Typography variant="h2" className="mb-4">Rent</Typography>
          {rentDue.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No rent records yet.</p>
          ) : (
            <ul className="space-y-3">
              {pendingRent.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-700/30 p-4"
                >
                  <div>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {MONTHS[r.month - 1]} {r.year}
                    </span>
                    <span className="ml-2 text-slate-500 dark:text-slate-400">— ₹{r.amount.toLocaleString("en-IN")}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setMarkPaidModal({ rentId: r.id, month: r.month, year: r.year })}
                  >
                    Mark as paid
                  </Button>
                </li>
              ))}
              {paidRent.slice(0, 5).map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-600 p-4 opacity-90"
                >
                  <div>
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {MONTHS[r.month - 1]} {r.year}
                    </span>
                    <span className="ml-2 text-emerald-600 dark:text-emerald-400">Paid</span>
                    {r.paidDate && (
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                        {new Date(r.paidDate).toLocaleDateString("en-IN")}
                        {r.paymentMode && ` · ${r.paymentMode.replace("_", " ")}`}
                      </span>
                    )}
                  </div>
                  <span className="text-slate-600 dark:text-slate-400">₹{r.amount.toLocaleString("en-IN")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Expenses to pay */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6">
          <Typography variant="h2" className="mb-4">Expenses (your share)</Typography>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            These expenses are assigned to your unit. Pay as agreed with your landlord.
          </p>
          {expensesToPay.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No expenses assigned to you.</p>
          ) : (
            <ul className="space-y-3">
              {expensesToPay.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-600 p-4"
                >
                  <div>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {CATEGORY_LABELS[e.category] ?? e.category}
                    </span>
                    <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                      {new Date(e.date).toLocaleDateString("en-IN")}
                    </span>
                    {e.notes && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{e.notes}</p>}
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">₹{e.amount.toLocaleString("en-IN")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 sm:p-6">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            When you mark rent as paid, your landlord can see the payment details (date, mode, notes) in their dashboard.
          </p>
        </div>
      </main>

      <Modal
        isOpen={!!markPaidModal}
        onClose={() => setMarkPaidModal(null)}
        title={markPaidModal ? `Mark ${MONTHS[markPaidModal.month - 1]} ${markPaidModal.year} as paid` : undefined}
      >
        {markPaidModal && (
          <div className="space-y-4">
            <div>
              <Select
                label="Payment mode"
                options={PAYMENT_MODES.map((mode) => ({ value: mode, label: mode.replace("_", " ") }))}
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as typeof PAYMENT_MODES[number])}
              />
            </div>
            <Input
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reference ID, transaction ID, etc."
            />
            <div className="flex gap-3 pt-2">
              <Button variant="secondary" onClick={() => setMarkPaidModal(null)}>
                Cancel
              </Button>
              <Button onClick={handleMarkPaid} isLoading={markPaidMutation.isPending}>
                Mark as paid
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
